"use client";

import { Capacitor } from "@capacitor/core";
import { Download } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { shareViaSystemSheet } from "@/lib/native/share";

export function DocumentDownload({
  href,
  documentNumber,
  label = "Download",
}: {
  href: string;
  documentNumber: string;
  label?: string;
}) {
  const [pending, setPending] = useState(false);
  const download = async () => {
    if (pending) return;
    setPending(true);
    try {
      const response = await fetch(href, { cache: "no-store" });
      if (
        !response.ok ||
        !response.headers.get("content-type")?.includes("application/pdf")
      ) {
        throw new Error("Unable to download the document. Please try again.");
      }
      const blob = await response.blob();
      const providedName = response.headers
        .get("content-disposition")
        ?.match(/filename="([^"]+)"/)?.[1];
      const fileName = (providedName ?? `${documentNumber}.pdf`).replace(
        /[^a-zA-Z0-9._-]/g,
        "-",
      );
      if (Capacitor.isNativePlatform()) {
        const result = await shareViaSystemSheet({
          title: documentNumber,
          dialogTitle: "Save or share document",
          asset: {
            file: new File([blob], fileName, { type: "application/pdf" }),
            type: "pdf",
          },
        });
        if (!result.ok && result.reason !== "aborted")
          throw new Error("Unable to save the document. Please try again.");
      } else {
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = fileName;
        document.body.append(anchor);
        anchor.click();
        anchor.remove();
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Unable to download the document.",
      );
    } finally {
      setPending(false);
    }
  };
  return (
    <button
      type="button"
      disabled={pending}
      onClick={download}
      className="inline-flex items-center gap-2 rounded-xl border border-current/20 px-3 py-2 text-sm disabled:opacity-50"
      aria-label={`Download ${documentNumber}`}
      aria-busy={pending}
    >
      <Download className="size-4" aria-hidden />
      {pending ? "Downloading…" : label}
    </button>
  );
}
